/**
 * Lebensmittel, Gerichte, Wochenplan und Tagesprotokoll — **immer lokal zuerst**.
 *
 * Der wichtigste Teil steht in `logMeal`: Was gegessen wurde, wird mit
 * **eingefrorenen** Nährwerten und eingefrorenem Namen abgelegt. Ein Verweis auf
 * das Rezept würde genügen — bis das Rezept geändert wird und damit rückwirkend
 * die Vergangenheit umschreibt. Ein Protokoll, das sich nachträglich ändert, ist
 * kein Protokoll.
 */

import {
  cleanNutrients,
  isoDateInZone,
  macrosForAmount,
  macrosPerPortion,
  newId,
  normalizeSteps,
  nutrientsForAmount,
  nutrientsPerPortion,
  plannedLogId,
  roundMacros,
  scaleMacros,
  scaleNutrients,
  summarizeDay,
} from '@kraftwerk/shared';
import type {
  DaySummary,
  Macros,
  FoodLogEstimate,
  Nutrients,
  MacroTargets,
  DaySlot,
  MealSlot,
  ShelfCategory,
} from '@kraftwerk/shared';
import { db } from './local.js';
import { listSkips, skipMeal, unskipMeal } from './meals.js';
import type {
  KraftwerkDatabase,
  LocalFood,
  LocalFoodLogEntry,
  LocalMealPlanEntry,
  LocalRecipe,
  LocalRecipeCookNote,
  LocalRecipeIngredient,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';

// --- Lebensmittel ------------------------------------------------------------

export interface FoodDraft {
  name: string;
  brand?: string | null;
  kcalPer100g: number;
  proteinPer100g: number;
  carbsPer100g: number;
  fatPer100g: number;
  portionName?: string | null;
  portionGrams?: number | null;
  sourceNote?: string | null;
  barcode?: string | null;
  isFavorite?: boolean;
  category?: ShelfCategory;
  /** Weitere Nährstoffe je 100 g; `null`/fehlend = unbekannt. */
  nutrients?: Nutrients | null;
}

export async function listFoods(database: KraftwerkDatabase = db): Promise<LocalFood[]> {
  const rows = await database.foods.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort(
      (a, b) => Number(b.isFavorite) - Number(a.isFavorite) || a.name.localeCompare(b.name, 'de'),
    );
}

export async function createFood(
  draft: FoodDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalFood> {
  const now = stamp();
  const row: LocalFood = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    name: draft.name.trim(),
    brand: draft.brand ?? null,
    kcalPer100g: draft.kcalPer100g,
    proteinPer100g: draft.proteinPer100g,
    carbsPer100g: draft.carbsPer100g,
    fatPer100g: draft.fatPer100g,
    portionName: draft.portionName ?? null,
    portionGrams: draft.portionGrams ?? null,
    // Die Packung ist die beste Quelle, die es gibt — und sie liegt in der Hand.
    sourceNote: draft.sourceNote ?? null,
    barcode: draft.barcode ?? null,
    isFavorite: draft.isFavorite ?? false,
    category: draft.category ?? 'other',
    nutrients: draft.nutrients ?? null,
  };
  return writeRow('foods', row, 'upsert', database);
}

export async function updateFood(
  id: string,
  changes: Partial<FoodDraft>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.foods.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow('foods', { ...existing, ...changes, updatedAt: stamp() }, 'upsert', database);
}

export async function deleteFood(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.foods.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow('foods', { ...existing, deletedAt: now, updatedAt: now }, 'delete', database);
}

// --- Gerichte ----------------------------------------------------------------

export interface RecipeDraft {
  name: string;
  slot: MealSlot;
  portions?: number;
  kcalPerPortion?: number | null;
  proteinPerPortion?: number | null;
  carbsPerPortion?: number | null;
  fatPerPortion?: number | null;
  /** Weitere Nährstoffe je Portion (nur bei eingetragenen Werten). */
  nutrients?: Nutrients | null;
  items?: string | null;
  steps?: string[] | null;
  note?: string | null;
  sourceNote?: string | null;
  isFavorite?: boolean;
  prepNote?: string | null;
  sourceUrl?: string | null;
}

export async function listRecipes(database: KraftwerkDatabase = db): Promise<LocalRecipe[]> {
  const rows = await database.recipes.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort(
      (a, b) => Number(b.isFavorite) - Number(a.isFavorite) || a.name.localeCompare(b.name, 'de'),
    );
}

export async function createRecipe(
  draft: RecipeDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalRecipe> {
  const now = stamp();
  const row: LocalRecipe = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    catalogKey: null,
    name: draft.name.trim(),
    slot: draft.slot,
    portions: draft.portions ?? 1,
    // Ohne Zutaten gelten die eingetragenen Werte. Sobald welche dazukommen,
    // schaltet `syncRecipeMacroSource` um — sichtbar in der Oberfläche.
    macroSource: 'entered',
    kcalPerPortion: draft.kcalPerPortion ?? null,
    proteinPerPortion: draft.proteinPerPortion ?? null,
    carbsPerPortion: draft.carbsPerPortion ?? null,
    fatPerPortion: draft.fatPerPortion ?? null,
    nutrients: draft.nutrients ?? null,
    items: draft.items ?? null,
    steps: draft.steps ?? null,
    note: draft.note ?? null,
    sourceNote: draft.sourceNote ?? null,
    isFavorite: draft.isFavorite ?? false,
    prepNote: draft.prepNote ?? null,
    sourceUrl: draft.sourceUrl ?? null,
  };
  return writeRow('recipes', row, 'upsert', database);
}

export async function updateRecipe(
  id: string,
  changes: Partial<RecipeDraft> & { macroSource?: LocalRecipe['macroSource'] },
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.recipes.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow('recipes', { ...existing, ...changes, updatedAt: stamp() }, 'upsert', database);
}

export async function deleteRecipe(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const recipe = await database.recipes.get(id);
  if (!recipe || recipe.deletedAt) return;

  const now = stamp();
  const ingredients = (
    await database.recipe_ingredients.where('recipeId').equals(id).toArray()
  ).filter((row) => !row.deletedAt);
  const notes = (await database.recipe_cook_notes.where('recipeId').equals(id).toArray()).filter(
    (row) => !row.deletedAt,
  );

  await writeRows(
    [
      { table: 'recipes', row: { ...recipe, deletedAt: now, updatedAt: now }, kind: 'delete' },
      ...ingredients.map((row) => ({
        table: 'recipe_ingredients' as const,
        row: { ...row, deletedAt: now, updatedAt: now },
        kind: 'delete' as const,
      })),
      ...notes.map((row) => ({
        table: 'recipe_cook_notes' as const,
        row: { ...row, deletedAt: now, updatedAt: now },
        kind: 'delete' as const,
      })),
    ],
    database,
  );
  // Bereits protokollierte Mahlzeiten bleiben unangetastet: Sie tragen ihre
  // Nährwerte selbst und sind Vergangenheit, kein Verweis.
}

export async function listIngredients(
  recipeId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalRecipeIngredient[]> {
  const rows = await database.recipe_ingredients.where('recipeId').equals(recipeId).toArray();
  return rows.filter((row) => !row.deletedAt).sort((a, b) => a.position - b.position);
}

/**
 * Zutat aus der Bibliothek. `portionCount` heißt: eingegeben als Portionen des
 * Lebensmittels („2 Scheiben") — `grams` ist dann daraus gerechnet und bleibt
 * eingefroren, auch wenn sich die Portion am Lebensmittel später ändert.
 */
export async function addIngredient(
  recipeId: string,
  foodId: string,
  grams: number,
  database: KraftwerkDatabase = db,
  portionCount: number | null = null,
): Promise<void> {
  const existing = await listIngredients(recipeId, database);
  const now = stamp();
  const row: LocalRecipeIngredient = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    recipeId,
    foodId,
    grams,
    portionCount,
    label: null,
    position: nextPosition(existing),
  };
  await writeRow('recipe_ingredients', row, 'upsert', database);
  await syncRecipeMacroSource(recipeId, database);
}

/** Freie Zeile („1 Prise Salz") — ohne Lebensmittel, ohne Nährwerte. */
export async function addFreeIngredient(
  recipeId: string,
  label: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const text = label.trim();
  if (!text) return;
  const existing = await listIngredients(recipeId, database);
  const now = stamp();
  const row: LocalRecipeIngredient = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    recipeId,
    foodId: null,
    grams: null,
    portionCount: null,
    label: text,
    position: nextPosition(existing),
  };
  await writeRow('recipe_ingredients', row, 'upsert', database);
}

/**
 * Eine freie Zeile einem Lebensmittel zuordnen — an derselben Stelle. So wird
 * aus „200 g rote Linsen" (übernommen von einer Webseite) eine Zutat mit
 * Nährwerten, ohne die Reihenfolge zu verlieren.
 */
export async function assignIngredient(
  id: string,
  input: { foodId: string; grams: number; portionCount: number | null },
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.recipe_ingredients.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'recipe_ingredients',
    {
      ...existing,
      foodId: input.foodId,
      grams: input.grams,
      portionCount: input.portionCount,
      label: null,
      updatedAt: stamp(),
    },
    'upsert',
    database,
  );
  await syncRecipeMacroSource(existing.recipeId, database);
}

/** Menge ändern — Gramm, oder Portionen samt daraus gerechneten Gramm. */
export async function updateIngredientAmount(
  id: string,
  amount: { grams: number; portionCount: number | null },
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.recipe_ingredients.get(id);
  if (!existing || existing.deletedAt || existing.foodId === null) return;
  await writeRow(
    'recipe_ingredients',
    { ...existing, grams: amount.grams, portionCount: amount.portionCount, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Eine Zeile nach oben oder unten — getauscht wird mit dem Nachbarn, beide
 * Zeilen in einem Zug, damit die Reihenfolge nie halb geändert ankommt.
 */
export async function moveIngredient(
  id: string,
  direction: -1 | 1,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await database.recipe_ingredients.get(id);
  if (!row || row.deletedAt) return;
  const list = await listIngredients(row.recipeId, database);
  const index = list.findIndex((entry) => entry.id === id);
  const other = list[index + direction];
  if (index < 0 || !other) return;
  const now = stamp();
  // Positionen neu durchzählen — ältere Zeilen können doppelte Positionen tragen.
  const order = list.map((entry) => entry.id);
  order[index] = other.id;
  order[index + direction] = id;
  await writeRows(
    list
      .map((entry) => ({ entry, position: order.indexOf(entry.id) }))
      .filter(({ entry, position }) => entry.position !== position)
      .map(({ entry, position }) => ({
        table: 'recipe_ingredients' as const,
        row: { ...entry, position, updatedAt: now },
        kind: 'upsert' as const,
      })),
    database,
  );
}

function nextPosition(existing: readonly LocalRecipeIngredient[]): number {
  return existing.reduce((max, entry) => Math.max(max, entry.position + 1), 0);
}

/** Schritte als Ganzes speichern — leere fallen weg, die Reihenfolge ist die der Liste. */
export async function setRecipeSteps(
  recipeId: string,
  steps: readonly string[],
  database: KraftwerkDatabase = db,
): Promise<void> {
  const list = normalizeSteps(steps);
  await updateRecipe(recipeId, { steps: list.length > 0 ? list : null }, database);
}

/** Schritte eines Gerichts, gleich ob als Liste oder (vor E9) als Freitext gespeichert. */
export function stepsOf(recipe: Pick<LocalRecipe, 'steps'>): string[] {
  return normalizeSteps(recipe.steps);
}

/**
 * „Als Variante anlegen": eine Kopie mit Zutaten und Schritten, in einem Zug
 * geschrieben. Ohne Bild (eine Variante bekommt meist ein eigenes) und ohne
 * Kochnotizen (die gehören zum Original). Gibt die neue Kennung zurück.
 */
export async function duplicateRecipe(
  id: string,
  name: string,
  database: KraftwerkDatabase = db,
): Promise<string | null> {
  const original = await database.recipes.get(id);
  if (!original || original.deletedAt) return null;
  const now = stamp();
  const copyId = newId();
  const ingredients = await listIngredients(id, database);

  await writeRows(
    [
      {
        table: 'recipes',
        row: {
          ...original,
          id: copyId,
          name: name.trim() || original.name,
          isFavorite: false,
          catalogKey: null,
          imageAt: null,
          steps: normalizeSteps(original.steps).length > 0 ? normalizeSteps(original.steps) : null,
          updatedAt: now,
          deletedAt: null,
        },
        kind: 'upsert',
      },
      ...ingredients.map((entry) => ({
        table: 'recipe_ingredients' as const,
        row: { ...entry, id: newId(), recipeId: copyId, updatedAt: now, deletedAt: null },
        kind: 'upsert' as const,
      })),
    ],
    database,
  );
  return copyId;
}

/**
 * Gericht aus einem Webseiten-Import anlegen: Zutatenzeilen als freie Zeilen
 * (zuordnen kommt danach), Schritte als Liste, Quelle als Link — in einem Zug.
 * Nährwerte gibt es noch keine; die Seite zeigt das an.
 */
/** „Nährwerte: Angabe von chefkoch.de“ — die Seite ist die Quelle, nicht die App. */
function importSourceNote(url: string): string {
  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    // Keine gültige Adresse: dann eben die ganze.
  }
  return `Nährwerte: Angabe von ${host}`;
}

export async function createFromImport(
  input: {
    name: string;
    slot: MealSlot;
    portions: number | null;
    ingredients: readonly string[];
    steps: readonly string[];
    sourceUrl: string;
    /** Nährwerte je Portion von der Seite — nur vollständig, sonst `null`. */
    nutrition?: {
      kcal: number;
      protein: number;
      carbs: number;
      fat: number;
      nutrients: Nutrients;
    } | null;
  },
  database: KraftwerkDatabase = db,
): Promise<string> {
  const now = stamp();
  const recipeId = newId();
  const steps = normalizeSteps([...input.steps]);
  await writeRows(
    [
      {
        table: 'recipes',
        row: {
          id: recipeId,
          updatedAt: now,
          deletedAt: null,
          catalogKey: null,
          name: input.name.trim(),
          slot: input.slot,
          portions: input.portions ?? 1,
          macroSource: 'entered',
          // Bis 16.10.2026 fielen die Nährwerte der Seite weg, obwohl sie
          // maschinenlesbar dastanden (Chefkoch). Jetzt mit Quelle.
          kcalPerPortion: input.nutrition?.kcal ?? null,
          proteinPerPortion: input.nutrition?.protein ?? null,
          carbsPerPortion: input.nutrition?.carbs ?? null,
          fatPerPortion: input.nutrition?.fat ?? null,
          nutrients: input.nutrition ? cleanNutrients(input.nutrition.nutrients) : null,
          items: null,
          steps: steps.length > 0 ? steps : null,
          note: null,
          sourceNote: input.nutrition ? importSourceNote(input.sourceUrl) : null,
          isFavorite: false,
          prepNote: null,
          sourceUrl: input.sourceUrl,
        },
        kind: 'upsert',
      },
      ...input.ingredients
        .map((line) => line.trim())
        .filter(Boolean)
        .map((label, position) => ({
          table: 'recipe_ingredients' as const,
          row: {
            id: newId(),
            updatedAt: now,
            deletedAt: null,
            recipeId,
            foodId: null,
            grams: null,
            portionCount: null,
            label,
            position,
          },
          kind: 'upsert' as const,
        })),
    ],
    database,
  );
  return recipeId;
}

// --- Kochnotizen -------------------------------------------------------------

export async function addCookNote(
  input: { recipeId: string; cookedOn: string; rating: 1 | -1 | null; note: string | null },
  database: KraftwerkDatabase = db,
): Promise<LocalRecipeCookNote> {
  const now = stamp();
  const row: LocalRecipeCookNote = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    recipeId: input.recipeId,
    cookedOn: input.cookedOn,
    rating: input.rating,
    note: input.note?.trim() || null,
  };
  return writeRow('recipe_cook_notes', row, 'upsert', database);
}

/** Neueste zuerst — sie ist die, die beim nächsten Kochen zählt. */
export async function listCookNotes(
  recipeId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalRecipeCookNote[]> {
  const rows = await database.recipe_cook_notes.where('recipeId').equals(recipeId).toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => b.cookedOn.localeCompare(a.cookedOn) || b.updatedAt.localeCompare(a.updatedAt));
}

export async function removeCookNote(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const row = await database.recipe_cook_notes.get(id);
  if (!row || row.deletedAt) return;
  const now = stamp();
  await writeRow(
    'recipe_cook_notes',
    { ...row, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}

export async function removeIngredient(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.recipe_ingredients.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'recipe_ingredients',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
  await syncRecipeMacroSource(existing.recipeId, database);
}

/**
 * Hat das Gericht Zutaten, gelten die Zutaten — sonst die eingetragenen Werte.
 *
 * Die Umschaltung passiert automatisch, wird aber **angezeigt**: Sonst wundert
 * sich jemand, warum sein eingetragener Wert nicht mehr auftaucht.
 */
async function syncRecipeMacroSource(recipeId: string, database: KraftwerkDatabase): Promise<void> {
  const recipe = await database.recipes.get(recipeId);
  if (!recipe || recipe.deletedAt) return;

  // Nur Lebensmittel zählen: Ein Gericht aus lauter freien Zeilen („Salz",
  // „Öl") hätte gerechnet 0 kcal — dann gelten weiter die eingetragenen Werte.
  const ingredients = await listIngredients(recipeId, database);
  const next = ingredients.some((entry) => entry.foodId !== null) ? 'ingredients' : 'entered';
  if (recipe.macroSource === next) return;

  await writeRow(
    'recipes',
    { ...recipe, macroSource: next, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Nährwerte einer Portion — gerechnet oder eingetragen.
 *
 * `null` heißt **unbekannt**: ein Gericht ohne Zutaten und ohne eingetragene
 * Werte. Die Oberfläche schreibt das hin, statt eine Null zu zeigen.
 */
export async function macrosOfRecipe(
  recipe: LocalRecipe,
  database: KraftwerkDatabase = db,
): Promise<Macros | null> {
  if (recipe.macroSource === 'ingredients') {
    // Freie Zeilen („1 Prise Salz") tragen keine Nährwerte — sie zählen nicht mit.
    const ingredients = (await listIngredients(recipe.id, database)).filter(
      (entry): entry is LocalRecipeIngredient & { foodId: string; grams: number } =>
        entry.foodId !== null && entry.grams !== null,
    );
    const foods = await database.foods.bulkGet(ingredients.map((entry) => entry.foodId));
    const inputs = ingredients.flatMap((entry, index) => {
      const food = foods[index];
      if (!food || food.deletedAt) return [];
      return [
        {
          per100g: {
            kcal: food.kcalPer100g,
            protein: food.proteinPer100g,
            carbs: food.carbsPer100g,
            fat: food.fatPer100g,
          },
          grams: entry.grams,
        },
      ];
    });
    return macrosPerPortion(inputs, recipe.portions);
  }

  if (recipe.kcalPerPortion === null) return null;
  return {
    kcal: recipe.kcalPerPortion,
    protein: recipe.proteinPerPortion ?? 0,
    carbs: recipe.carbsPerPortion ?? 0,
    fat: recipe.fatPerPortion ?? 0,
  };
}

/**
 * Weitere Nährstoffe je Portion: aus Zutaten nur, was **jede** Zutat kennt;
 * sonst, was am Gericht eingetragen ist.
 */
export async function nutrientsOfRecipe(
  recipe: LocalRecipe,
  database: KraftwerkDatabase = db,
): Promise<Nutrients> {
  if (recipe.macroSource !== 'ingredients') return cleanNutrients(recipe.nutrients);
  const ingredients = (await listIngredients(recipe.id, database)).filter(
    (entry): entry is LocalRecipeIngredient & { foodId: string; grams: number } =>
      entry.foodId !== null && entry.grams !== null,
  );
  const foods = await database.foods.bulkGet(ingredients.map((entry) => entry.foodId));
  return nutrientsPerPortion(
    ingredients.map((entry, index) => ({
      per100g: foods[index] && !foods[index]!.deletedAt ? foods[index]!.nutrients : null,
      grams: entry.grams,
    })),
    recipe.portions,
  );
}

// --- Wochenplan --------------------------------------------------------------

export async function listPlannedMeals(
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalMealPlanEntry[]> {
  const rows = await database.meal_plan_entries.where('date').equals(date).toArray();
  return rows.filter((row) => !row.deletedAt);
}

export async function planMeal(
  input: {
    date: string;
    slot: DaySlot;
    recipeId?: string | null;
    foodId?: string | null;
    portions?: number;
    grams?: number | null;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalMealPlanEntry> {
  const now = stamp();
  const row: LocalMealPlanEntry = {
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    date: input.date,
    slot: input.slot,
    recipeId: input.recipeId ?? null,
    foodId: input.foodId ?? null,
    portions: input.portions ?? 1,
    grams: input.grams ?? null,
    note: null,
  };
  const written = await writeRow('meal_plan_entries', row, 'upsert', database);
  // Geplant zählt als gegessen (18.10.2026) — ausgelassen wird ausdrücklich.
  await logPlanned(written, database);
  return written;
}

/**
 * Menge eines Planeintrags ändern — Portionen beim Gericht, Gramm beim
 * Lebensmittel. Die Protokollzeile zieht mit, auch heute: das ist eine
 * bewusste Änderung, keine Rezeptänderung im Hintergrund. Ausgelassenes
 * bleibt ausgelassen.
 */
export async function changePlannedAmount(
  planId: string,
  amount: number,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  const existing = await database.meal_plan_entries.get(planId);
  if (!existing || existing.deletedAt || !(amount > 0)) return false;
  const plan = await writeRow(
    'meal_plan_entries',
    {
      ...existing,
      ...(existing.recipeId ? { portions: amount } : { grams: amount }),
      updatedAt: stamp(),
    },
    'upsert',
    database,
  );
  const row = await database.food_log.get(plannedLogId(planId));
  if (row?.deletedAt) return true;
  const input = await plannedLogInput(plan, database);
  if (!input) return false;
  await writeLogEntry(input, database, { id: plannedLogId(planId), eatenAt: row?.eatenAt });
  return true;
}

export async function removePlannedMeal(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.meal_plan_entries.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'meal_plan_entries',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
  await removeLogEntry(plannedLogId(id), database);
}

// --- Geplant zählt als gegessen (18.10.2026) ---------------------------------
//
// Was im Wochenplan steht, steht als Zeile im Protokoll — mit fester Kennung
// je Planeintrag (`plannedLogId`). Vorher musste jeder Planeintrag im Tag
// einzeln „gegessen“ werden, Supplements und Mahlzeiten verhielten sich
// verschieden. Jetzt markiert man nur, was **nicht** stattfand: Auslassen
// löscht die Zeile weich, Zurücknehmen belebt sie wieder. Eingefroren wird
// mit dem Tag: Bis dahin zieht die Zeile Rezeptänderungen nach
// (`syncPlannedLog`), ab dem Tag selbst nicht mehr.

type LogInput = Parameters<typeof writeLogEntry>[0];

function recipeLogInput(
  recipe: LocalRecipe,
  perPortion: Macros,
  nutrients: Nutrients,
  target: { date: string; slot: DaySlot; portions: number },
): LogInput {
  return {
    date: target.date,
    slot: target.slot,
    recipeId: recipe.id,
    foodId: null,
    label: recipe.name,
    portions: target.portions,
    grams: null,
    macros: scaleMacros(perPortion, target.portions),
    nutrients: scaleNutrients(nutrients, target.portions),
  };
}

function foodLogInput(
  food: LocalFood,
  target: { date: string; slot: DaySlot; grams: number },
): LogInput {
  return {
    date: target.date,
    slot: target.slot,
    recipeId: null,
    foodId: food.id,
    label: food.name,
    portions: null,
    grams: target.grams,
    macros: macrosForAmount(
      {
        kcal: food.kcalPer100g,
        protein: food.proteinPer100g,
        carbs: food.carbsPer100g,
        fat: food.fatPer100g,
      },
      target.grams,
    ),
    nutrients: nutrientsForAmount(food.nutrients, target.grams),
  };
}

/**
 * Werte eines Planeintrags, wie sie ins Protokoll gingen. `null` heißt
 * unbekannt — ein Gericht ohne Nährwerte oder ein Lebensmittel ohne Menge
 * zählt nicht still mit 0.
 */
export async function plannedLogInput(
  plan: LocalMealPlanEntry,
  database: KraftwerkDatabase = db,
): Promise<LogInput | null> {
  if (plan.recipeId) {
    const recipe = await database.recipes.get(plan.recipeId);
    if (!recipe || recipe.deletedAt) return null;
    const perPortion = await macrosOfRecipe(recipe, database);
    if (!perPortion) return null;
    const nutrients = await nutrientsOfRecipe(recipe, database);
    return recipeLogInput(recipe, perPortion, nutrients, {
      date: plan.date,
      slot: plan.slot,
      portions: plan.portions,
    });
  }
  if (plan.foodId) {
    const food = await database.foods.get(plan.foodId);
    if (!food || food.deletedAt) return null;
    const grams = plan.grams ?? (food.portionGrams ? food.portionGrams * plan.portions : null);
    if (!grams) return null;
    return foodLogInput(food, { date: plan.date, slot: plan.slot, grams });
  }
  return null;
}

/** Die Protokollzeile eines Planeintrags anlegen; `null`, wenn die Werte unbekannt sind. */
async function logPlanned(
  plan: LocalMealPlanEntry,
  database: KraftwerkDatabase,
): Promise<LocalFoodLogEntry | null> {
  const input = await plannedLogInput(plan, database);
  if (!input) return null;
  return writeLogEntry(input, database, { id: plannedLogId(plan.id) });
}

function sameValues(row: LocalFoodLogEntry, input: LogInput): boolean {
  const macros = roundMacros(input.macros);
  const nutrients = input.nutrients ? cleanNutrients(input.nutrients) : {};
  return (
    row.label === input.label &&
    row.kcal === macros.kcal &&
    row.protein === macros.protein &&
    row.carbs === macros.carbs &&
    row.fat === macros.fat &&
    JSON.stringify(row.nutrients ?? {}) === JSON.stringify(nutrients)
  );
}

/**
 * Plan und Protokoll eines Tages abgleichen — ab heute, nie rückwirkend.
 *
 * - fehlt die Zeile eines Planeintrags, entsteht sie (Pläne von vor dem
 *   18.10.2026; nicht, wenn die Mahlzeit ausgelassen ist oder dasselbe schon
 *   von Hand eingetragen wurde — sonst zählte es doppelt);
 * - eine weich gelöschte Zeile ist **ausgelassen** und bleibt es;
 * - künftige Tage ziehen Rezeptänderungen nach, heute ist eingefroren;
 * - gehört eine Zeile zu einem gelöschten Planeintrag, geht sie mit.
 */
export async function syncPlannedLog(
  date: string,
  todayDate: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  if (date < todayDate) return;
  const [plans, rows, skips] = await Promise.all([
    database.meal_plan_entries.where('date').equals(date).toArray(),
    database.food_log.where('date').equals(date).toArray(),
    listSkips(date, database),
  ]);
  const skipped = new Set(skips.map((row) => row.slot));
  const rowById = new Map(rows.map((row) => [row.id, row]));
  for (const plan of plans) {
    const id = plannedLogId(plan.id);
    const existing = rowById.get(id);
    if (plan.deletedAt) {
      if (existing && !existing.deletedAt) await removeLogEntry(id, database);
      continue;
    }
    if (existing?.deletedAt) continue;
    if (!existing) {
      if (skipped.has(plan.slot)) continue;
      const manual = rows.some(
        (row) =>
          !row.deletedAt &&
          row.slot === plan.slot &&
          ((plan.recipeId !== null && row.recipeId === plan.recipeId) ||
            (plan.foodId !== null && row.foodId === plan.foodId)),
      );
      if (manual) continue;
    } else if (date === todayDate) {
      continue;
    }
    const input = await plannedLogInput(plan, database);
    if (!input || (existing && sameValues(existing, input))) continue;
    await writeLogEntry(input, database, { id, eatenAt: existing?.eatenAt });
  }
}

/** Einen Planeintrag auslassen — er zählt nicht mehr, bleibt aber im Plan. */
export async function skipPlanned(planId: string, database: KraftwerkDatabase = db): Promise<void> {
  await removeLogEntry(plannedLogId(planId), database);
}

/** Auslassen zurücknehmen — mit den Werten von damals, sonst neu gerechnet. */
export async function restorePlanned(
  plan: LocalMealPlanEntry,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  const existing = await database.food_log.get(plannedLogId(plan.id));
  if (existing && !existing.deletedAt) return true;
  if (existing) {
    await writeRow(
      'food_log',
      { ...existing, deletedAt: null, updatedAt: stamp() },
      'upsert',
      database,
    );
    return true;
  }
  return (await logPlanned(plan, database)) !== null;
}

/** Eine ganze Mahlzeit auslassen — samt dem, was für sie geplant war. */
export async function skipSlot(
  date: string,
  slot: DaySlot,
  database: KraftwerkDatabase = db,
  ownerId?: string | null,
): Promise<void> {
  await skipMeal(date, slot, database, ownerId);
  for (const plan of await listPlannedMeals(date, database)) {
    if (plan.slot === slot) await skipPlanned(plan.id, database);
  }
}

export async function unskipSlot(
  date: string,
  slot: DaySlot,
  database: KraftwerkDatabase = db,
  ownerId?: string | null,
): Promise<void> {
  await unskipMeal(date, slot, database, ownerId);
  for (const plan of await listPlannedMeals(date, database)) {
    if (plan.slot === slot) await restorePlanned(plan, database);
  }
}

// --- Tagesprotokoll ----------------------------------------------------------

export async function listLog(
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalFoodLogEntry[]> {
  const rows = await database.food_log.where('date').equals(date).toArray();
  return rows.filter((row) => !row.deletedAt).sort((a, b) => a.eatenAt.localeCompare(b.eatenAt));
}

/**
 * Ein Gericht protokollieren — mit eingefrorenen Werten.
 *
 * Gibt `null` zurück, wenn die Nährwerte unbekannt sind. Ein Eintrag mit 0 kcal
 * wäre schlimmer als keiner: Er sähe aus wie eine Angabe.
 */
export async function logRecipe(
  input: { recipe: LocalRecipe; date: string; slot?: DaySlot; portions?: number },
  database: KraftwerkDatabase = db,
): Promise<LocalFoodLogEntry | null> {
  const perPortion = await macrosOfRecipe(input.recipe, database);
  if (!perPortion) return null;
  const nutrients = await nutrientsOfRecipe(input.recipe, database);
  return writeLogEntry(
    recipeLogInput(input.recipe, perPortion, nutrients, {
      date: input.date,
      slot: input.slot ?? input.recipe.slot,
      portions: input.portions ?? 1,
    }),
    database,
  );
}

export async function logFood(
  input: { food: LocalFood; date: string; slot: DaySlot; grams: number },
  database: KraftwerkDatabase = db,
): Promise<LocalFoodLogEntry> {
  return writeLogEntry(foodLogInput(input.food, input), database);
}

/**
 * Freier Eintrag ohne Vorlage — „auswärts gegessen, geschätzt".
 *
 * Kommt der Wert aus der Foto-Schätzung (`estimate: 'photo'`), trägt die
 * Zeile das dauerhaft mit — auch wenn die Zahlen vorher bearbeitet wurden.
 * Die angenommene Portion steht dann in der Notiz.
 */
export async function logFreeEntry(
  input: {
    date: string;
    slot: DaySlot;
    label: string;
    macros: Macros;
    estimate?: FoodLogEstimate | null;
    note?: string | null;
    /**
     * Foto-Schätzung im Hintergrund (28.10.2026): Kennung des Auftrags und
     * wann das Foto entstand — so erkennt jedes Gerät, dass der Platzhalter
     * übernommen ist.
     */
    fixed?: { id: string; eatenAt: string };
  },
  database: KraftwerkDatabase = db,
): Promise<LocalFoodLogEntry> {
  return writeLogEntry(
    {
      date: input.date,
      slot: input.slot,
      recipeId: null,
      foodId: null,
      label: input.label.trim(),
      portions: null,
      grams: null,
      macros: input.macros,
      estimate: input.estimate ?? null,
      note: input.note?.trim() ? input.note.trim().slice(0, 500) : null,
    },
    database,
    input.fixed ?? {},
  );
}

/**
 * Einen früheren Eintrag noch einmal eintragen — mit **seinen** eingefrorenen
 * Werten, nicht den heutigen des Rezepts: „das Gleiche wie neulich" heißt das
 * Gleiche, auch wenn das Rezept inzwischen anders rechnet.
 */
export async function relogEntry(
  entry: LocalFoodLogEntry,
  target: { date: string; slot: DaySlot },
  database: KraftwerkDatabase = db,
): Promise<LocalFoodLogEntry> {
  return writeLogEntry(
    {
      date: target.date,
      slot: target.slot,
      recipeId: entry.recipeId,
      foodId: entry.foodId,
      label: entry.label,
      portions: entry.portions,
      grams: entry.grams,
      macros: { kcal: entry.kcal, protein: entry.protein, carbs: entry.carbs, fat: entry.fat },
      nutrients: entry.nutrients ?? null,
      // Eine Schätzung bleibt eine Schätzung, auch beim „Wie gestern“.
      estimate: entry.estimate ?? null,
      note: entry.estimate ? entry.note : null,
    },
    database,
  );
}

/** „Wie gestern": alle Einträge einer Mahlzeit vom Vortag übernehmen. */
export async function copyMeal(
  from: { date: string; slot: DaySlot },
  toDate: string,
  database: KraftwerkDatabase = db,
): Promise<number> {
  const entries = (await listLog(from.date, database)).filter((entry) => entry.slot === from.slot);
  for (const entry of entries) {
    await relogEntry(entry, { date: toDate, slot: from.slot }, database);
  }
  return entries.length;
}

/**
 * Direkt aus Open Food Facts ins Protokoll, in Gramm — ohne Bibliothek. Nur mit
 * allen vier Nährwerten: Ein fehlender Wert würde sonst als 0 verbucht, und das
 * wäre erfunden. `null` heißt: unvollständig, nichts geschrieben.
 */
export async function logSuggestion(
  input: {
    suggestion: {
      name: string;
      brand: string | null;
      kcalPer100g: number | null;
      proteinPer100g: number | null;
      carbsPer100g: number | null;
      fatPer100g: number | null;
      nutrients?: Nutrients | null;
    };
    grams: number;
    date: string;
    slot: DaySlot;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalFoodLogEntry | null> {
  const { suggestion } = input;
  if (
    suggestion.kcalPer100g === null ||
    suggestion.proteinPer100g === null ||
    suggestion.carbsPer100g === null ||
    suggestion.fatPer100g === null
  ) {
    return null;
  }
  return writeLogEntry(
    {
      date: input.date,
      slot: input.slot,
      recipeId: null,
      foodId: null,
      label: suggestion.brand ? `${suggestion.name} (${suggestion.brand})` : suggestion.name,
      portions: null,
      grams: input.grams,
      macros: macrosForAmount(
        {
          kcal: suggestion.kcalPer100g,
          protein: suggestion.proteinPer100g,
          carbs: suggestion.carbsPer100g,
          fat: suggestion.fatPer100g,
        },
        input.grams,
      ),
      nutrients: nutrientsForAmount(suggestion.nutrients, input.grams),
    },
    database,
  );
}

async function writeLogEntry(
  input: {
    date: string;
    slot: DaySlot;
    recipeId: string | null;
    foodId: string | null;
    label: string;
    portions: number | null;
    grams: number | null;
    macros: Macros;
    /** Unbekannt bleibt unbekannt — `null` statt leerer Werte. */
    nutrients?: Nutrients | null;
    /** Geschätzt (Foto, KI) — sichtbar an der Zeile und in der Tagessumme. */
    estimate?: FoodLogEstimate | null;
    note?: string | null;
  },
  database: KraftwerkDatabase,
  /** Feste Kennung und Zeitpunkt — für die Zeile eines Planeintrags. */
  fixed: { id?: string; eatenAt?: string | undefined } = {},
): Promise<LocalFoodLogEntry> {
  const now = stamp();
  const macros = roundMacros(input.macros);
  const row: LocalFoodLogEntry = {
    id: fixed.id ?? newId(),
    updatedAt: now,
    deletedAt: null,
    date: input.date,
    slot: input.slot,
    eatenAt: fixed.eatenAt ?? now,
    recipeId: input.recipeId,
    foodId: input.foodId,
    label: input.label,
    portions: input.portions,
    grams: input.grams,
    ...macros,
    nutrients:
      input.nutrients && Object.keys(cleanNutrients(input.nutrients)).length > 0
        ? cleanNutrients(input.nutrients)
        : null,
    note: input.note ?? null,
    estimate: input.estimate ?? null,
  };
  return writeRow('food_log', row, 'upsert', database);
}

export async function removeLogEntry(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.food_log.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow('food_log', { ...existing, deletedAt: now, updatedAt: now }, 'delete', database);
}

/** Der Kalendertag in der Zeitzone des Nutzers — nie der des Servers. */
export function today(timeZone: string, now: Date = new Date()): string {
  return isoDateInZone(now, timeZone);
}

export async function daySummaryFor(
  date: string,
  targets: MacroTargets,
  database: KraftwerkDatabase = db,
): Promise<DaySummary> {
  const entries = await listLog(date, database);
  return summarizeDay(
    entries.map((entry) => ({
      slot: entry.slot,
      macros: { kcal: entry.kcal, protein: entry.protein, carbs: entry.carbs, fat: entry.fat },
    })),
    targets,
  );
}
